import register from 'preact-custom-element'
import { initValidate } from 'libs/validate'
import { useEffect, useRef, useState } from 'preact/hooks'
import { getHeight, getTopOffset, hideModal, on, openModal, select } from 'helpers/dom'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import TextInput from 'snippets/form/text-input/text-input'
import Icon from 'snippets/icon/icon'

const selectors = {
  SPACING_BORDER: 2,
  MODAL_ID: 'back-in-stock',
  HEADER_SELECTOR: '.js-header-sticky',
  PRODUCT_RELATED_SELECTOR: 'product-related',
  PRODUCT_NOTIFY_MODAL: '.js-modal-product-notify'
}

function ProductNotify() {
  const noticeRef = useRef(null)
  const formProductNotify = useRef(null)
  const checkboxAcceptMarketing = useRef(null)
  const [notify, setNotify] = useState('')
  const [notifyState, setNotifyState] = useState('')
  const [productId, setProductId] = useState(null)
  const [variantId, setVariantId] = useState(null)
  const [formValidated, setFormValidated] = useState(null)
  const [closeNotice, setCloseNotice] = useState(null)
  const { title, notifyMeWhenAvailable, findSimilarProducts, description, notifyMeEmailLabel, notifyMeMarketingLabel } = translate.product
  let notifyMessage = ''

  useEffect(() => {
    const formValidated = initValidate(formProductNotify.current, true)

    setFormValidated(formValidated)
  }, [])

  useEffect(() => {
    if (!formValidated) return

    globalEmitter.on(emitterProps.product.notify, (data) => {
      setProductId(data.id)
      setVariantId(data.variantId)
      openModal(selectors.MODAL_ID)
    })

    formValidated.onSuccess(onSubmit)
    scrollToNotice()
  }, [formValidated, variantId, productId])

  useEffect(() => {
    const modalEl = select(selectors.PRODUCT_NOTIFY_MODAL)

    on(
      'close-modal',
      () => {
        formProductNotify.current.reset()
        setNotify('')
      },
      modalEl
    )
  }, [])

  notify &&
  notifyState &&
  (notifyMessage = (
    <div ref={noticeRef} className={`relative mt-4 notice-main lg:mt-6 ${closeNotice ? 'hidden' : ''}`}>
      <div className={`min-w-1.5 h-full absolute left-0 rounded-l -ml-px ${notifyState === 'error' ? 'bg-error-content' : 'bg-success-content'}`}></div>
      {notifyState === 'error'
        ? <Icon className="size-8 text-error-content" name="icon-error" viewBox="0 0 32 32" />
        : <Icon className="size-8 text-success-content" name="icon-success" viewBox="0 0 32 32" />
      }
      <div className="notice-main-content text-left pl-4">{notify}</div>
      <button onClick={() => setCloseNotice(true)} type="button" className="ml-auto mr-4" title="close notice">
        <Icon className="size-6 text-grey-700 !ml-0" name="icon-close" viewBox="0 0 24 24" />
      </button>
    </div>
  ))

  const onScrollToSimilarProducts = () => {
    const productRelatedEl = select(selectors.PRODUCT_RELATED_SELECTOR)
    const headerEl = select(selectors.HEADER_SELECTOR)

    hideModal(selectors.MODAL_ID)

    const offset = getTopOffset(productRelatedEl) - getHeight(headerEl) + selectors.SPACING_BORDER

    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    })
  }

  const scrollToNotice = () => {
    setTimeout(() => {
      if (noticeRef.current) {
        noticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }

  const onSubmit = async () => {
    if (!window.BIS || !variantId || !productId) return

    const formData = new FormData(formProductNotify.current)
    const email = formData.get('email')

    try {
      if (checkboxAcceptMarketing.current?.checked) {
        const { portalId, newsletterFormId } = window.GM_STATE.apps.hubSpot || {}

        if (portalId && newsletterFormId) {
          await fetch(
            `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${newsletterFormId}`,
            {
              method: 'POST',
              body: JSON.stringify({ fields: [...formData.entries()].map(([name, value]) => ({ name, value })) }),
              headers: { 'Content-Type': 'application/json' }
            }
          )
        }
      }

      const res = await window.BIS.create(email, variantId, productId)

      setCloseNotice(false)

      if (res.status === 'OK') {
        setNotifyState('success')
        setNotify(res.message)
        scrollToNotice()
      } else {
        throw new Error(res.errors?.base || 'An error occurred')
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Form submission failed:', error)
      setNotifyState('error')
      setNotify(error.message || 'An unexpected error occurred')
      scrollToNotice()
    }
  }

  return (
    <div className="text-center">
      <p className="text-lg font-semibold text-grey-900 md:text-2xl">{title}</p>
      {GM_STATE.shopify.template === 'product' ? (
        <button
          onClick={onScrollToSimilarProducts}
          title={findSimilarProducts}
          type="button"
          className="button-primary mt-4 w-full"
        >
          {findSimilarProducts}
        </button>
      ) : (
        <a href={GM_STATE.product.similar} title={findSimilarProducts} className="button-primary mt-4 w-full">
          {findSimilarProducts}
        </a>
      )}
      <hr className="relative my-9 overflow-visible border-grey-200 text-base text-grey-900 after:absolute after:-top-3 after:left-1/2 after:-translate-x-1/2 after:bg-white after:px-2 after:content-['Or']"></hr>
      <form id="ProductNotify" className="flex flex-col gap-4" ref={formProductNotify}>
        <div className="text-base">{description}</div>
        <TextInput
          classWrap="w-full text-left"
          type="email"
          id="product-notify-input"
          placeholder={notifyMeEmailLabel}
          autocomplete={true}
          name="email"
          required
        />
        <div class="checkbox-input relative flex w-full items-center">
          <input
            id="accept-marketing"
            class="checkbox-input__input"
            type="checkbox"
            name="marketing"
            value="accept-marketing"
            ref={checkboxAcceptMarketing}
          />
          <label for="accept-marketing" class="checkbox-input__label">
            {notifyMeMarketingLabel}
          </label>
        </div>
        <button type="submit" className="button-primary">
          {notifyMeWhenAvailable}
        </button>
      </form>
      {notifyMessage}
    </div>
  )
}

if (!customElements.get('product-notify')) {
  register(ProductNotify, 'product-notify')
}
